/**
 * Stage 3 — inland lakes. Lakes are flooded into low interior basins (local elevation minima away
 * from the coast), grown hex by hex into the lowest adjacent ground. A lake is only accepted if the
 * remaining land stays one connected mass, so lakes never cut the continent. Finally every lake
 * (including small enclosed waters left by the landmass stage) gets a flat surface just below its
 * lowest shore, which also makes lakes valid river outlets.
 */
import type { Rng } from '../core/rng';
import { Terrain } from '../core/types';
import { clamp, isLand, landHexes, type MapContext } from './context';
import { bfs } from './paths';

/** Minimum hex distance between two lakes. */
const LAKE_SPACING = 7;

export function placeLakes(ctx: MapContext, rng: Rng): void {
  const { grid, n, terrain, elevation } = ctx;
  const land = landHexes(ctx);
  const count = Math.round(clamp(land.length / 700 + rng.range(0, 1.2), 1, 7));
  const maxSize = Math.round(clamp(land.length / 450, 2, 7));

  const waterHexes: number[] = [];
  const landMask = new Uint8Array(n);
  for (let h = 0; h < n; h++) {
    if (isLand(terrain[h])) landMask[h] = 1;
    else waterHexes.push(h);
  }
  const waterDist = bfs(grid, waterHexes, landMask).dist;

  // Basin score: low ground that sits below its neighbourhood, with a little jitter for variety.
  const sortedElev = land.map((h) => elevation[h]).sort((a, b) => a - b);
  const lowCut = sortedElev[Math.floor(sortedElev.length * 0.6)];
  const score = new Float64Array(n);
  const candidates: number[] = [];
  for (const h of land) {
    if (waterDist[h] < 3 || elevation[h] > lowCut) continue;
    let around = 0;
    let k = 0;
    grid.forNeighbours(h, (m) => {
      around += elevation[m];
      k++;
    });
    score[h] = around / k - elevation[h] - 0.15 * elevation[h] + rng.range(0, 6);
    candidates.push(h);
  }
  candidates.sort((a, b) => score[b] - score[a] || a - b);

  const lakeHexes: number[] = [];
  for (let h = 0; h < n; h++) if (terrain[h] === Terrain.Lake) lakeHexes.push(h);
  let placed = 0;
  for (const seed of candidates) {
    if (placed >= count) break;
    if (!isLand(terrain[seed]) || lakeHexes.some((l) => grid.distance(l, seed) < LAKE_SPACING)) continue;
    const lake = growLake(ctx, seed, rng.int(1, maxSize), waterDist);
    if (!keepsLandConnected(ctx, lake)) continue;
    for (const h of lake) terrain[h] = Terrain.Lake;
    lakeHexes.push(...lake);
    placed++;
  }
  setLakeLevels(ctx);
}

/** Floods from `seed` into the lowest adjacent hexes that stay clear of other water. */
function growLake(ctx: MapContext, seed: number, size: number, waterDist: Int32Array): number[] {
  const { grid, elevation } = ctx;
  const lake = [seed];
  const inLake = new Set(lake);
  while (lake.length < size) {
    let best = -1;
    for (const h of lake) {
      grid.forNeighbours(h, (m) => {
        if (inLake.has(m) || waterDist[m] < 2) return;
        if (best < 0 || elevation[m] < elevation[best] || (elevation[m] === elevation[best] && m < best))
          best = m;
      });
    }
    if (best < 0) break;
    lake.push(best);
    inLake.add(best);
  }
  return lake;
}

function keepsLandConnected(ctx: MapContext, lake: number[]): boolean {
  const { n, terrain, grid } = ctx;
  const passable = new Uint8Array(n);
  let total = 0;
  let start = -1;
  for (let h = 0; h < n; h++) {
    if (isLand(terrain[h])) {
      passable[h] = 1;
      total++;
    }
  }
  for (const h of lake) {
    passable[h] = 0;
    total--;
  }
  for (let h = 0; h < n && start < 0; h++) if (passable[h]) start = h;
  if (start < 0) return false;
  const { dist } = bfs(grid, [start], passable);
  let reached = 0;
  for (let h = 0; h < n; h++) if (dist[h] >= 0) reached++;
  return reached === total;
}

/** Sets every lake's surface to 3 below its lowest shore hex. */
function setLakeLevels(ctx: MapContext): void {
  const { grid, n, terrain, elevation } = ctx;
  const seen = new Uint8Array(n);
  for (let s = 0; s < n; s++) {
    if (terrain[s] !== Terrain.Lake || seen[s]) continue;
    const body = [s];
    seen[s] = 1;
    let shore = Infinity;
    for (let i = 0; i < body.length; i++) {
      grid.forNeighbours(body[i], (m) => {
        if (terrain[m] === Terrain.Lake) {
          if (!seen[m]) {
            seen[m] = 1;
            body.push(m);
          }
        } else if (isLand(terrain[m])) shore = Math.min(shore, elevation[m]);
      });
    }
    const level = shore === Infinity ? 60 : shore - 3;
    for (const h of body) elevation[h] = level;
  }
}
